package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TrainingCategoryManagerScreen(
    onBack: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    var categories by remember { mutableStateOf<List<TrainingCategory>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var categoryName by remember { mutableStateOf("") }
    var subCategoryName by remember { mutableStateOf("") }
    var isSaving by remember { mutableStateOf(false) }
    var categoryToDelete by remember { mutableStateOf<TrainingCategory?>(null) }

    fun loadCategories() {
        scope.launch {
            isLoading = true
            repository.getTrainingCategories().onSuccess { list ->
                categories = list
            }.onFailure {
                categories = emptyList()
            }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) {
        loadCategories()
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text("Manage Training Categories") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .padding(padding)
                .fillMaxSize()
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // Add Category Form
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.medium,
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
            ) {
                Column(
                    modifier = Modifier.padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text("Add New Category / Subcategory", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                    
                    OutlinedTextField(
                        value = categoryName,
                        onValueChange = { categoryName = it },
                        label = { Text("Category Name *") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = MaterialTheme.shapes.small
                    )

                    OutlinedTextField(
                        value = subCategoryName,
                        onValueChange = { subCategoryName = it },
                        label = { Text("Subcategory Name (Optional)") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = MaterialTheme.shapes.small
                    )

                    Button(
                        onClick = {
                            if (categoryName.isNotBlank()) {
                                val req = SaveTrainingCategoryRequest(
                                    categoryName = categoryName.trim(),
                                    subCategoryName = subCategoryName.ifBlank { null }
                                )
                                scope.launch {
                                    isSaving = true
                                    repository.saveTrainingCategory(req).onSuccess {
                                        categoryName = ""
                                        subCategoryName = ""
                                        loadCategories()
                                        snackbarHostState.showSnackbar("Category saved successfully")
                                    }.onFailure {
                                        snackbarHostState.showSnackbar("Failed to save category")
                                    }
                                    isSaving = false
                                }
                            }
                        },
                        enabled = categoryName.isNotBlank() && !isSaving,
                        modifier = Modifier.align(Alignment.End),
                        shape = MaterialTheme.shapes.small
                    ) {
                        if (isSaving) {
                            CircularProgressIndicator(modifier = Modifier.size(18.dp), color = MaterialTheme.colorScheme.onPrimary)
                        } else {
                            Text("Add Category")
                        }
                    }
                }
            }

            Text("Existing Categories (${categories.size})", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

            if (isLoading) {
                Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                    BrandLoadingSpinner()
                }
            } else if (categories.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                    Text("No categories found. Create one above.", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.outline)
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxWidth().weight(1f),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    items(items = categories, key = { it.id }) { cat ->
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = MaterialTheme.shapes.small,
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(12.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                                    Text(
                                        text = cat.categoryName,
                                        style = MaterialTheme.typography.titleSmall,
                                        fontWeight = FontWeight.Bold
                                    )
                                    if (!cat.subCategoryName.isNullOrBlank()) {
                                        Text(
                                            text = "${cat.subCategoryName}",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.primary
                                        )
                                    }
                                }
                                IconButton(onClick = { categoryToDelete = cat }) {
                                    CloseIcon(tint = MaterialTheme.colorScheme.error)
                                }
                            }
                        }
                    }
                }
            }
        }

        categoryToDelete?.let { cat ->
            AlertDialog(
                onDismissRequest = { categoryToDelete = null },
                title = { Text("Delete Category") },
                text = { Text("Are you sure you want to delete '${cat.categoryName}${cat.subCategoryName?.let { " -> $it" } ?: ""}'? This will also delete any training items in this category.") },
                confirmButton = {
                    Button(
                        onClick = {
                            val cId = cat.id
                            categoryToDelete = null
                            categories = categories.filter { it.id != cId }
                            scope.launch {
                                repository.deleteTrainingCategory(cId)
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                    ) {
                        Text("Delete")
                    }
                },
                dismissButton = {
                    TextButton(onClick = { categoryToDelete = null }) {
                        Text("Cancel")
                    }
                },
                shape = MaterialTheme.shapes.large
            )
        }
    }
}
